// uses youtube iframe API to play video

import * as _ from "underscore";

let initialised = false;
let options;
let player;
let enabled = true;
// The YouTube iframe API loads asynchronously and only attaches the player
// methods (playVideo, seekTo, ...) once the player fires onReady, so every
// call into `player` must wait for readiness.
let playerReady = false;
// A play requested before the player was ready, applied on onReady.
let pendingPlay: { time?: number } | undefined;
// Incremented whenever the player is replaced or reset, so that callbacks
// from a superseded player (whose iframe can still fire onReady/onStateChange
// after we've moved on) can't mark the replacement ready or drive its events.
let playerGeneration = 0;

const isPlayerReady = () => Boolean(player) && playerReady;

const onPlayerStateChange = (event) => {
  if (event.data === 1 || event.data === 2) {
    timeUpdate();
  } else if (event.data === 0) {
    if (options.onEnded) {
      options.onEnded();
    }
  } else if (event.data === 3) {
    if (options.onBuffering) {
      options.onBuffering();
    }
  }
  return;
};

const createPlayer = () => {
  // loadVideoById is only attached once the player is ready.
  if (isPlayerReady() && options.playerElement.is("iframe")) {
    player.loadVideoById({
      videoId: options.youTubeID,
      startSeconds: options.start,
      endSeconds: options.end,
    });
    return;
  }

  // The YouTube IFrame API replaces the original <div> with an <iframe> when
  // the player is first created. If we end up here with the iframe still in
  // the DOM (e.g. after an in-page refresh that nulled out `player` via reset()
  // but left the iframe behind because the wrapper component is shouldComponentUpdate=false),
  // a second `new YT.Player(...)` call would just wrap the already-wrapped
  // iframe and return a broken player. Replace the stale iframe with a fresh
  // <div> so YT.Player can build a clean player from scratch.
  const elementId = options.playerElement.attr("id");
  const existing = elementId ? document.getElementById(elementId) : null;
  if (existing && existing.tagName === "IFRAME") {
    const fresh = document.createElement("div");
    fresh.id = elementId;
    existing.parentNode?.replaceChild(fresh, existing);
  }

  const emittedOnReady = false;

  playerReady = false;
  const generation = ++playerGeneration;
  player = new window.YT.Player(elementId, {
    width: options.playerElement.width(),
    videoId: options.youTubeID,
    events: {
      onReady: function () {
        if (generation !== playerGeneration) {
          return;
        }
        playerReady = true;
        if (pendingPlay) {
          const { time } = pendingPlay;
          pendingPlay = undefined;
          play(time);
        }
      },
      onStateChange: function (state) {
        if (generation !== playerGeneration || !enabled) {
          return;
        }
        if (!emittedOnReady) {
          const duration = player.getDuration();
          if (duration > 0) {
            if (options.onReady) {
              options.onReady(player.getDuration());
            }
            // TODO: should we be setting this to true?
            //emittedOnReady;
          }
        }
        onPlayerStateChange(state);
      },
    },
    start: options.start,
    end: options.end,
    playerVars: { autoplay: 1, start: Math.round(options.start), rel: 0 },
  });
};

export const loadVideo = (_options) => {
  options = _options;
  enabled = true;

  if (window.YT?.Player) {
    createPlayer();
    return;
  }

  if (!initialised) {
    // 2. This code loads the IFrame Player API code asynchronously.
    const tag = document.createElement("script");

    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      // If the player was torn down (disable/reset) while the API script was
      // still loading, don't construct an autoplaying player for it.
      if (enabled) {
        createPlayer();
      }
    };
    initialised = true;
  }
  // else: the API script is still loading; onYouTubeIframeAPIReady will
  // create the player with the latest `options`.
};

let updateTimer;
var timeUpdate = _.throttle(() => {
  if (!enabled || !player) {
    return;
  }

  if (options.onTimeUpdate && player.getCurrentTime) {
    options.onTimeUpdate(player.getCurrentTime());
  }

  if (player.getPlayerState && player.getPlayerState() === 1) {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(timeUpdate, 50);
  }
}, 50);

export const play = (time) => {
  enabled = true;
  if (!isPlayerReady()) {
    pendingPlay = { time };
    return;
  }
  if (time) {
    seekTo(time);
  }
  player.playVideo();
};

export const pause = () => {
  enabled = true;
  // A pause overrides any play still waiting on the player to become ready.
  pendingPlay = undefined;
  if (isPlayerReady()) {
    player.pauseVideo();
  }
};

export const status = () => {
  if (!player || !player.getPlayerState) {
    return {
      paused: true,
    };
  }

  if (player.getPlayerState() === 1) {
    return {
      playing: true,
    };
  } else {
    return {
      paused: true,
    };
  }
};

export const disable = () => {
  enabled = false;
  // disable() is the teardown path (called by reset() and by
  // SyncAudioController.remove()), so cancel all deferred work here: a play
  // queued while the player was unready must not restart a disabled player
  // when onReady arrives, and throttled trailing seeks / time updates must
  // not fire into a replacement player created after a reset.
  pendingPlay = undefined;
  seekTo.cancel();
  timeUpdate.cancel();
  clearTimeout(updateTimer);
};

// var getDuration = function () {
//   return player.getDuration();
// };

export const reset = () => {
  disable();
  // We intentionally don't call player.destroy() here — destroy removes the
  // iframe from the DOM, and the wrapper component that owns the slot won't
  // re-render to provide a fresh element (shouldComponentUpdate=false).
  // createPlayer() replaces the stale iframe with a fresh div instead.
  player = null;
  playerReady = false;
  playerGeneration++;
};

export const jumpBack = (time) => {
  if (!isPlayerReady()) {
    return;
  }
  time = time || 5;
  seekTo(Math.max(0, player.getCurrentTime() - time));
};

export const jumpForward = (time) => {
  if (!isPlayerReady()) {
    return;
  }
  time = time || 5;
  seekTo(player.getCurrentTime() + time);
};

var seekTo = _.throttle((time) => {
  // disable() cancels pending trailing calls; re-check readiness anyway as
  // defense in depth.
  if (isPlayerReady()) {
    player.seekTo(time);
  }
}, 200);

export const setSpeed = (speed) => {
  if (isPlayerReady()) {
    player.setPlaybackRate(speed);
  }
};

let slowSpeed = false;
export const toggleSpeed = () => {
  if (!isPlayerReady()) {
    return;
  }
  if (slowSpeed) {
    setSpeed(1);
  } else {
    setSpeed(0.5);
  }
  slowSpeed = !slowSpeed;
};
